import { ReactNode, useState } from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import toast from '@liquidweb-dev/undercurrent/toast';
import type { BillingCycleT } from '@liquidweb-dev/club-sauce/types/billingCycle';
import { useQueryClient } from '@tanstack/react-query';
import BuyNowPaymentMethod from 'components/BuyNowPaymentMethod';
import ErrorState from 'components/ErrorState';
import useCartItemAdd from 'modules/queries/market/cart/item/useAdd';
import useCartClaim from 'modules/queries/market/cart/useClaim';
import useCartOrder from 'modules/queries/market/cart/useOrder';
import useAssetDetails, {
	ASSET_DETAILS_QUERY_KEY,
} from 'modules/queries/asset/useDetails';
import usePackageDetails from 'modules/queries/market/package/useDetails';
import {
	useAssetDdosProtectionUpsell,
	useAssetImunify360Upsell,
	useAssetThreatdownUpsell,
} from 'modules/upsell/useUpsellState';
import useSendHubspotEvent from 'utility/gtm/hubspot/useEvent';
import ProductDetailsCard from './ProductDetailsCard';
import Wrapper, { WrapperProps } from './Wrapper';

export type AddOnCode =
	| 'PKG-DDoS.Server.Protect'
	| 'PKG-ThreatDown'
	| 'PKG-Imunify360'
	| 'PKG-AcronisEDR';

interface PackageDisplayInfo {
	name: string;
	description: string;
	subtitle?: string;
}

const packageDisplayInfo: Record<AddOnCode, PackageDisplayInfo> = {
	'PKG-DDoS.Server.Protect': {
		name: 'Advanced DDoS Protection',
		description:
			'Better protection against Layer 4 Attacks that are volumetric up to 10 Gbps',
	},
	'PKG-ThreatDown': {
		name: 'ThreatDown',
		description:
			'Endpoint Detection and Response (EDR), Application Block (AB), Vulnerability and Patch Management (VPM)',
	},
	'PKG-Imunify360': {
		name: 'Imunify 360',
		description:
			'Antivirus, Firewall, WAF, PHP Security Layer, Patch Management, Domain Reputation with automation.',
	},
	'PKG-AcronisEDR': {
		name: 'Endpoint Detection & Response',
		subtitle: 'powered by Acronis',
		description:
			'Protect your endpoints from sophisticated threats with proactive detection and response',
	},
};

interface CycleOption {
	label: ReactNode;
	value: BillingCycleT;
}

interface AddOnContentProps {
	code: AddOnCode;
	cycles: CycleOption[];
	initialSelectedCycle: BillingCycleT;
	onBuyClick: (cycle: BillingCycleT) => void;
	buyIsDisabled: boolean;
}

const AddOnContent = ({
	code,
	cycles,
	initialSelectedCycle,
	onBuyClick,
	buyIsDisabled,
}: AddOnContentProps) => {
	const [selectedCycle, setSelectedCycle] =
		useState<BillingCycleT>(initialSelectedCycle);

	return (
		<>
			<DrawerContent>
				<ProductDetailsCard
					code={code}
					description={packageDisplayInfo[code]?.description}
					name={packageDisplayInfo[code]?.name}
					subtitle={packageDisplayInfo[code]?.subtitle}
					selectedCycle={selectedCycle}
				/>
			</DrawerContent>

			<DrawerFooter sx={{ gap: 200 }}>
				<BuyNowPaymentMethod
					cycles={cycles}
					cycleValue={selectedCycle}
					onCycleChange={(event) =>
						setSelectedCycle(event.target.value as BillingCycleT)
					}
				/>

				<Button
					fullWidth
					onClick={() => onBuyClick(selectedCycle)}
					variant="primary"
					color="highlight"
					disabled={buyIsDisabled}
					size="large"
				>
					Buy now
				</Button>
			</DrawerFooter>
		</>
	);
};

export interface DrawerAddOnProps extends WrapperProps {
	/** Callback function when purchase is successful */
	onSuccess: () => void;
	/** Callback function when drawer is closed */
	onClose: () => void;
	/** The product/package code of the add-on */
	code?: AddOnCode;
	/** The ID of the region to add the add-on to (should be the related subaccnt's region) */
	regionId: number;
	/** The unique ID of the server to add the add-on to */
	uniqId: string;
}

const DrawerAddOn = ({
	code,
	regionId,
	uniqId,
	onSuccess,
	...drawerProps
}: DrawerAddOnProps) => {
	const isOpen = Boolean(drawerProps.open);
	const queryClient = useQueryClient();

	const {
		data: packageDetails,
		isLoading: isPackageDetailsLoading,
		isError: isPackageDetailsError,
		refetch: refetchPackageDetails,
	} = usePackageDetails(
		{
			code: code ?? '',
			alsowith: ['defaultPriceByCycle', 'defaultProductsConfiguration'],
		},
		{ enabled: Boolean(code) && isOpen },
	);

	const { mutateAsync: addCartItem, isLoading: isAddingCartItem } =
		useCartItemAdd();
	const { mutateAsync: claimCart, isLoading: isClaimingCart } = useCartClaim();
	const { mutateAsync: orderCart, isLoading: isOrderingCart } = useCartOrder();
	const [, setDdosUpsellDismissed] = useAssetDdosProtectionUpsell(uniqId, {
		enabled: isOpen,
	});
	const [, setImunify360Dismissed] = useAssetImunify360Upsell(uniqId, {
		enabled: isOpen,
	});
	const [, setThreatdownDismissed] = useAssetThreatdownUpsell(uniqId, {
		enabled: isOpen,
	});
	const { data: assetDetails, isLoading: isLoadingDetails } = useAssetDetails(
		{ uniq_id: uniqId },
		{ enabled: isOpen },
	);
	const sendAddonEvent = useSendHubspotEvent('addon', { enabled: isOpen });

	const buyIsDisabled =
		isAddingCartItem || isClaimingCart || isOrderingCart || isLoadingDetails;

	if (isPackageDetailsLoading) {
		return (
			<Wrapper {...drawerProps}>
				<DrawerContent>
					<Skeleton variant="rounded" height={400} width="100%" />
				</DrawerContent>
			</Wrapper>
		);
	}

	if (isPackageDetailsError) {
		return (
			<Wrapper {...drawerProps}>
				<DrawerContent sx={{ alignItems: 'center', justifyContent: 'center' }}>
					<ErrorState onTryAgain={refetchPackageDetails} />
				</DrawerContent>
			</Wrapper>
		);
	}

	const mostDiscountedCycle =
		packageDetails?.version.mostDiscountedCycle?.cycle;

	const cycles: CycleOption[] = (packageDetails?.version.cycles || []).map(
		({ cycle }) => ({
			label: (
				<span>
					Billed <strong>{cycle}</strong>
				</span>
			),
			value: cycle,
		}),
	);

	const handleBuyClick = async (cycle: BillingCycleT) => {
		if (!code) {
			throw new Error(
				'Code is required. This should never happen since we need a code to fetch the package details.',
			);
		}
		const setDismissUpsell = () => {
			if (code === 'PKG-DDoS.Server.Protect') {
				setDdosUpsellDismissed(true);
			}
			if (code === 'PKG-Imunify360') {
				setImunify360Dismissed(true);
			}
			if (code === 'PKG-ThreatDown') {
				setThreatdownDismissed(true);
			}
		};

		try {
			const cart = await addCartItem({
				cycle,
				item: {
					code,
					products: packageDetails.version.defaultProductsConfiguration?.map(
						(product) => ({
							code: product.product_code,
							package_to_product_id: product.package_to_product_id,
							region_id: regionId,
							properties: { related_subaccnt: uniqId },
						}),
					),
				},
			});

			await claimCart({ uuid: cart.uuid });
			await orderCart({ uuid: cart.uuid });

			toast.success('Your purchase has been completed');

			queryClient.invalidateQueries({
				queryKey: [ASSET_DETAILS_QUERY_KEY, { uniq_id: uniqId }],
			});

			setDismissUpsell();

			sendAddonEvent({
				serverUniqId: uniqId,
				serverHostName: assetDetails?.domain,
				addOnName: packageDisplayInfo[code]?.name,
			});

			onSuccess();
		} catch (_error) {
			// nothing
		}
	};

	return (
		<Wrapper {...drawerProps}>
			{code && (
				<AddOnContent
					code={code}
					cycles={cycles.length > 1 ? cycles : []}
					initialSelectedCycle={mostDiscountedCycle || 'monthly'}
					onBuyClick={handleBuyClick}
					buyIsDisabled={buyIsDisabled}
				/>
			)}
		</Wrapper>
	);
};

export default DrawerAddOn;
